// Syntax -> AST. Kent geen UmlShape/UmlConnector — puur tekst-regels omzetten
// naar platte tokens. Losse class van de Mapper zodat een toekomstig
// diagramtype (bv. sequence) alleen een nieuwe Mapper nodig heeft, geen
// wijziging hier (OCP, zie design V1).
const PACKAGE_LINE = /^package\s+"([^"]+)"\s*(\{)?/;
const ENTITY_LINE = /^(class|interface)\s+(\w+)(?:\s*<<([^>]+)>>)?\s*(\{)?/;
const MEMBER_LINE = /^([+\-#])\s*(.+)$/;
const CONNECTOR_LINE = /^(\w+)\s*(<\|--|<\|\.\.|<--|<\.\.|--\|>|\.\.\|>|-+>|\.+>)\s*(\w+)\s*(?::\s*(.+))?$/;
const CLOSE_BRACE = /^\}/;

// PlantUML lets you write an inheritance/realization arrow from either end
// ("Child --|> Parent" or "Parent <|-- Child" mean the same relation). Reverse
// arrows get swapped from/to + normalized to their forward form so downstream
// code (connectorKindFromArrow in the Mapper) only ever has to handle one shape.
const REVERSE_ARROW_MAP = {
  '<|--': '--|>',
  '<|..': '..|>',
  '<--': '-->',
  '<..': '..>',
};

class PlantUmlParser {
  parse(text) {
    const lines = text
      .split('\n')
      .map(function trimLine(line) {
        return line.trim();
      })
      .filter(function dropEmpty(line) {
        return line.length > 0 && line !== '@startuml' && line !== '@enduml';
      });

    const tokens = [];
    // Stack of open braces ({type: 'package'|'entity', node}) instead of two flat
    // variables — a flat currentPackage/currentClass pair got reset by ANY closing
    // brace, including a class's own `}` inside a package, which then also cleared
    // the package context for every class/interface after the first one in that
    // package (bug fixed 2026-07-18). The stack only pops the frame that's
    // actually closing.
    const scopeStack = [];

    lines.forEach(function parseLine(line) {
      const packageMatch = line.match(PACKAGE_LINE);
      const entityMatch = line.match(ENTITY_LINE);
      const memberMatch = line.match(MEMBER_LINE);
      const connectorMatch = line.match(CONNECTOR_LINE);
      const closeMatch = line.match(CLOSE_BRACE);

      if (packageMatch) {
        const packageNode = { kind: 'package', name: packageMatch[1], classes: [] };
        tokens.push(packageNode);
        if (packageMatch[2]) {
          scopeStack.push({ type: 'package', node: packageNode });
        }
        return;
      }
      if (entityMatch) {
        const entityNode = {
          kind: entityMatch[1], // 'class' | 'interface'
          name: entityMatch[2],
          stereotype: entityMatch[3] || null,
          members: [],
        };
        const openPackage = findOpenPackage(scopeStack);
        if (openPackage) {
          openPackage.classes.push(entityNode);
        } else {
          tokens.push(entityNode);
        }
        if (entityMatch[4]) {
          scopeStack.push({ type: 'entity', node: entityNode });
        }
        return;
      }
      if (memberMatch) {
        const openEntity = findOpenEntity(scopeStack);
        if (openEntity) {
          openEntity.members.push({ visibility: memberMatch[1], text: memberMatch[2] });
        }
        return;
      }
      if (connectorMatch) {
        tokens.push(buildConnectorToken(connectorMatch));
        return;
      }
      if (closeMatch) {
        scopeStack.pop();
      }
    });

    return tokens;
  }
}

/**
 * Nearest open package frame, searched top-down — a class/interface can be
 * the current top-of-stack frame while its enclosing package is one level down.
 * @param {{type: string, node: object}[]} scopeStack
 * @returns {object|null}
 */
function findOpenPackage(scopeStack) {
  for (let i = scopeStack.length - 1; i >= 0; i -= 1) {
    if (scopeStack[i].type === 'package') {
      return scopeStack[i].node;
    }
  }
  return null;
}

/**
 * The entity a member line belongs to — only valid when the entity frame is
 * the CURRENT top of stack (a member line right after a package brace with no
 * open class/interface has nowhere to go).
 * @param {{type: string, node: object}[]} scopeStack
 * @returns {object|null}
 */
function findOpenEntity(scopeStack) {
  const top = scopeStack[scopeStack.length - 1];
  return top && top.type === 'entity' ? top.node : null;
}

/**
 * @param {RegExpMatchArray} connectorMatch
 * @returns {{kind: 'connector', from: string, arrow: string, to: string, label: string|null}}
 */
function buildConnectorToken(connectorMatch) {
  let from = connectorMatch[1];
  let to = connectorMatch[3];
  let arrow = connectorMatch[2];
  const label = connectorMatch[4] || null;

  if (arrow.charAt(0) === '<') {
    const swap = from;
    from = to;
    to = swap;
    arrow = REVERSE_ARROW_MAP[arrow] || arrow;
  }

  return { kind: 'connector', from, arrow, to, label };
}

export default PlantUmlParser;
